import React, { useState } from "react";
import { Button, Input, useToast } from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import * as Yup from "yup";
import Modal from "@src/components/common/Modal";
import { ModalSizes } from "@src/components/common/Modal/ModalSizes";
import useYupFieldErrors from "@src/hooks/useYupFieldErrors";
import { useCurrentUserStore } from "@src/state/currentUserState";
import styles from "./AddClinicManualModal.module.scss";

const phoneRegex =
  /^\(?([0-9]{3})\)?\s*[-.]?\s*([0-9]{3})\s*[-.]?\s*([0-9]{4})$/;

const schema = Yup.object().shape({
  clinicName: Yup.string().trim().required("Clinic name is required."),
  address1: Yup.string().trim().required("Street address is required."),
  address2: Yup.string().trim().nullable(),
  city: Yup.string().trim().required("City is required."),
  state: Yup.string().trim().required("State is required."),
  postalCode: Yup.string().trim().required("Postal code is required."),
  phone: Yup.string()
    .trim()
    .required("Clinic phone number is required.")
    .test(
      "phone-format",
      "Phone number must be 10 digits.",
      (v) => !v || phoneRegex.test(v),
    ),
});

interface FormData {
  clinicName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
}

const INITIAL_DATA: FormData = {
  clinicName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  phone: "",
};

const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

interface Props {
  isShowing: boolean;
  onClose: () => void;
}

export default function AddClinicManualModal({
  isShowing,
  onClose,
}: Props): React.JSX.Element {
  const showToast = useToast();
  const { addClinicManual } = useCurrentUserStore();
  const { formData, handleUpdate, isValid } = useFormHandlers<FormData>(
    INITIAL_DATA,
    schema,
  );
  const { errors, validateField, applyServerFieldErrors } =
    useYupFieldErrors<FormData>(schema);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await addClinicManual(formData);
      showToast({
        message:
          "Your clinic has been submitted. VetGetRX will review it shortly.",
        variant: "success",
      });
      onClose();
    } catch (error: any) {
      const body = error?.response?.body;
      if (applyServerFieldErrors(body)) return;
      showToast({
        message:
          body?.detail ||
          error?.message ||
          "Something went wrong adding your clinic. Please try again.",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isShowing={isShowing}
      onClose={onClose}
      size={ModalSizes.Medium}
      header={<div className={styles.headerTitle}>Add a Clinic</div>}
    >
      <div className={styles.fieldStack}>
        <Field error={errors.clinicName}>
          <Input
            label="Clinic Name"
            defaultValue=""
            value={formData.clinicName}
            onChange={(value: string) =>
              handleUpdate({ name: "clinicName", value })
            }
            onBlur={() => validateField("clinicName", formData)}
            error={!!errors.clinicName}
            width="100%"
            required
            data-testid="add-clinic-manual-name"
          />
        </Field>

        <Field error={errors.address1}>
          <Input
            label="Clinic Address 01"
            defaultValue=""
            value={formData.address1}
            onChange={(value: string) =>
              handleUpdate({ name: "address1", value })
            }
            onBlur={() => validateField("address1", formData)}
            error={!!errors.address1}
            width="100%"
            required
            data-testid="add-clinic-manual-address-1"
          />
        </Field>

        <Field error={errors.address2}>
          <Input
            label="Clinic Address 02"
            defaultValue=""
            value={formData.address2}
            onChange={(value: string) =>
              handleUpdate({ name: "address2", value })
            }
            onBlur={() => validateField("address2", formData)}
            error={!!errors.address2}
            width="100%"
            data-testid="add-clinic-manual-address-2"
          />
        </Field>

        <Field error={errors.city}>
          <Input
            label="City"
            defaultValue=""
            value={formData.city}
            onChange={(value: string) =>
              handleUpdate({ name: "city", value })
            }
            onBlur={() => validateField("city", formData)}
            error={!!errors.city}
            width="100%"
            required
            data-testid="add-clinic-manual-city"
          />
        </Field>

        <div className={styles.row}>
          <Field error={errors.state}>
            <Input
              label="State"
              defaultValue=""
              value={formData.state}
              onChange={(value: string) =>
                handleUpdate({ name: "state", value })
              }
              onBlur={() => validateField("state", formData)}
              error={!!errors.state}
              width="100%"
              required
              data-testid="add-clinic-manual-state"
            />
          </Field>

          <Field error={errors.postalCode}>
            <Input
              label="Postal Code"
              defaultValue=""
              value={formData.postalCode}
              onChange={(value: string) =>
                handleUpdate({ name: "postalCode", value })
              }
              onBlur={() => validateField("postalCode", formData)}
              error={!!errors.postalCode}
              width="100%"
              required
              data-testid="add-clinic-manual-postal-code"
            />
          </Field>
        </div>

        <Field error={errors.phone}>
          <Input
            label="Clinic Phone Number"
            defaultValue=""
            type="tel"
            inputMode="numeric"
            value={formatPhone(formData.phone)}
            onChange={(value: string) =>
              handleUpdate({
                name: "phone",
                value: value.replace(/\D/g, "").slice(0, 10),
              })
            }
            onBlur={() => validateField("phone", formData)}
            error={!!errors.phone}
            width="100%"
            required
            data-testid="add-clinic-manual-phone"
          />
        </Field>
      </div>

      <Button
        className={`btn btn-primary btn-md ${styles.submitButton}`}
        onClick={handleSubmit}
        disabled={!isValid || isSubmitting}
        data-testid="add-clinic-manual-submit"
      >
        {isSubmitting ? "Adding Clinic..." : "Add Clinic"}
      </Button>
    </Modal>
  );
}

interface FieldProps {
  error?: string;
  children: React.ReactNode;
}

function Field({ error, children }: FieldProps): React.JSX.Element {
  return (
    <div className={styles.field}>
      {children}
      {error && (
        <p className={styles.errorMessage}>
          <i className="fa-light fa-circle-exclamation" aria-hidden />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
